import { useMemo } from "react";
import {
  ageFromBucket,
  catalogVolumeToBodymap,
  normalizeVolumeByMuscle,
  sessionEnergyReadout,
  volumeBySlugToHeatmap,
  type AgeBucket,
  type BodyFigure,
  type HeatmapRamp,
  type Session,
} from "@heraion/shared";
import { useSessionNotes } from "@heraion/ui";

import { apiFetch } from "./api";
import { useSessionResult } from "./useSessionResult";

// The Workouts detail pane's data for the selected session, split out of
// WorkoutsScreen.tsx (file-size cap, #368): the summary and its notes, the
// energy readout, the figure and the muscle heatmap. Same derivations.
export function useWorkoutDetailData(
  selectedId: string | null,
  items: Session[] | null,
  doc: Record<string, unknown> | null,
  heatmapRamp: HeatmapRamp,
) {
  // A correction keeps the session id (#38): its updatedAt is what changes.
  const selectedVersion = selectedId
    ? items?.find((s) => s.id === selectedId)?.updatedAt
    : undefined;
  const detail = useSessionResult(selectedId, selectedVersion);
  const detailNotes = useSessionNotes(apiFetch, selectedId, selectedVersion);
  // The full Session (with measured calories/HR + estimate): the SessionResult
  // detail lacks them; the list items ARE full Sessions, so pluck the selected
  // one (selection always comes from the loaded list). Web has no on-device
  // hcWins toggle → default ON (HC wins when present).
  const selectedSession = useMemo(
    () =>
      selectedId ? (items?.find((s) => s.id === selectedId) ?? null) : null,
    [selectedId, items],
  );
  // Web has no on-device hcWins toggle → default ON (HC wins when present). One
  // shared derivation of the readout, same as the mobile logger (HC-8 B2c).
  const energyReadout = selectedSession
    ? sessionEnergyReadout(selectedSession, {
        hcWins: true,
        ageYears: ageFromBucket(doc?.["ageBucket"] as AgeBucket | undefined),
      })
    : null;
  const figure = doc?.["preferredFigure"] as BodyFigure | undefined;

  // Muscle heatmap for the selected session, same derivation as the mobile
  // post-session card (volumeByMuscle → catalog vocab → 5-bucket ramp), so the
  // web session detail paints the identical figure.
  const bodyMapValue = useMemo(() => {
    if (detail.result === null) return null;
    return volumeBySlugToHeatmap(
      catalogVolumeToBodymap(normalizeVolumeByMuscle(detail.result.volumeByMuscle)),
      heatmapRamp,
    );
  }, [detail.result, heatmapRamp]);

  return { detail, detailNotes, energyReadout, figure, bodyMapValue };
}
